import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import AppContainer from "../../Components/Accounting/AppContainer";
import DataTable from "../../Components/Accounting/DataTable";
import ExportBar from "../../Components/Accounting/ExportBar";
import Select from "react-select";
import { RenderSecure } from "../../util/script/RenderSecure";
import ajaxAccounting from "../../util/accounting/remote/ajaxAccounting";
import functions from "../../util/accounting/functions";
import { canAddRow, addRowBlockedReason } from "../../util/accounting/formRows";
import toast, { Toaster } from "react-hot-toast";
import AppModal from "../../Components/Accounting/AppModal";

/** A voucher line is only an entry once it has an account and an amount. */
const JE_LINE_REQUIRED = [
  { key: "account_id", label: "an account" },
  { key: "amount", label: "an amount", numeric: true },
];

/** A line carries a debit or a credit, never both. */
const withAmount = (rows) =>
  rows.map((l) => ({ ...l, amount: (parseFloat(l.debit) || 0) || (parseFloat(l.credit) || 0) }));

const VOUCHER_TYPES = [
  { value: "PV", label: "Payment Voucher (PV)" },
  { value: "RV", label: "Receipt Voucher (RV)" },
  { value: "JV", label: "Journal Voucher (JV)" },
];

const Vouchers = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [vouchers,  setVouchers]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [fyList,    setFyList]    = useState([]);
  const [selectedFY, setSelectedFY] = useState(null);
  const [filterType, setFilterType] = useState(null);
  const [filterStatus, setFilterStatus] = useState(null);

  // Create modal state
  const [showCreate, setShowCreate] = useState(false);
  const [vType,   setVType]   = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [form, setForm] = useState({ voucher_date: new Date().toISOString().split("T")[0], description: "", reference: "", payee_name: "", notes: "" });
  const [accounts, setAccounts] = useState([]);
  const [lines, setLines] = useState([{ account_id: null, debit: "", credit: "", description: "", type: "debit" }, { account_id: null, debit: "", credit: "", description: "", type: "credit" }]);

  useEffect(() => { fetchFYs(); fetchAccounts(); }, []);
  useEffect(() => { if (selectedFY) fetchVouchers(); }, [selectedFY, filterType, filterStatus]);

  const fetchFYs = async () => {
    const res = await ajaxAccounting.listFinancialYears({});
    if (res.status === "OK") {
      const list = res.details || [];
      setFyList(list);
      const cur = list.find((y) => y.is_current === 1) || list[0];
      if (cur) setSelectedFY({ value: cur.id, label: cur.name });
    }
  };

  const fetchAccounts = async () => {
    const res = await ajaxAccounting.listAccounts({ postable_only: 1 });
    if (res.status === "OK") setAccounts(res.details || []);
  };

  const fetchVouchers = async () => {
    setLoading(true);
    const res = await ajaxAccounting.listVouchers({
      financial_year_id: selectedFY?.value,
      type:   filterType?.value   || undefined,
      status: filterStatus?.value || undefined,
    });
    if (res.status === "OK") setVouchers(Array.isArray(res.details) ? res.details : res.details?.list || []);
    else toast.error(res.message);
    setLoading(false);
  };

  const handleApprove = async (id) => {
    const res = await ajaxAccounting.approveVoucher({ id });
    if (res.status === "OK") { toast.success("Voucher approved!"); fetchVouchers(); }
    else toast.error(res.message);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this draft voucher? This cannot be undone.")) return;
    const res = await ajaxAccounting.deleteVoucher({ id });
    if (res.status === "OK") { toast.success("Voucher deleted."); fetchVouchers(); }
    else toast.error(res.message || "Failed to delete");
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!vType || !form.description) { toast.error("Type and description are required"); return; }
    const validLines = lines.filter((l) => l.account_id && (parseFloat(l.debit) || parseFloat(l.credit)));
    if (validLines.length < 1) { toast.error("Add at least one line with an account and amount"); return; }
    setSaving(true);
    const res = await ajaxAccounting.createVoucher({
      type: vType.value, ...form,
      financial_year_id: selectedFY?.value,
      lines: validLines.map((l) => ({
        account_id: l.account_id.value,
        debit: parseFloat(l.debit) || 0,
        credit: parseFloat(l.credit) || 0,
        description: l.description,
      })),
    });
    if (res.status === "OK") { toast.success("Voucher created!"); fetchVouchers(); setShowCreate(false); resetForm(); }
    else toast.error(res.message);
    setSaving(false);
  };

  const resetForm = () => {
    setVType(null);
    setForm({ voucher_date: new Date().toISOString().split("T")[0], description: "", reference: "", payee_name: "", notes: "" });
    setLines([{ account_id: null, debit: "", credit: "", description: "", type: "debit" }, { account_id: null, debit: "", credit: "", description: "", type: "credit" }]);
  };

  const accountOptions = accounts.map((a) => ({ value: a.id, label: `${a.account_code} — ${a.name}` }));
  const fyOptions      = fyList.map((y) => ({ value: y.id, label: y.name }));
  const statusOptions  = [{ value: "draft", label: "Draft" }, { value: "approved", label: "Approved" }, { value: "posted", label: "Posted" }];

  const typeBadge = (type) => {
    const colors = { PV: "#ef4444", RV: "#10b981", JV: "#6366f1" };
    return <span className="badge" style={{ background: (colors[type] || "#6b7280") + "20", color: colors[type] || "#6b7280", border: `1px solid ${colors[type] || "#6b7280"}40` }}>{type}</span>;
  };

  const statusBadge = (s) => {
    const m = { draft: "secondary", approved: "info", posted: "success", void: "danger" };
    return <span className={`badge bg-${m[s] || "secondary"}`}>{s}</span>;
  };

  const columns = [
    { key: "voucher_number", label: "Voucher #",  render: (v, r) => <strong>{v || `V-${r.id}`}</strong> },
    { key: "type",           label: "Type",        render: (v)    => typeBadge(v) },
    { key: "voucher_date",   label: "Date",        render: (v)    => functions.formatDate(v) },
    { key: "payee_name",     label: "Payee",       render: (v)    => v || "—" },
    { key: "description",    label: "Description", render: (v)    => <span className="tx-12">{v}</span> },
    { key: "total_amount",   label: "Amount",      render: (v)    => functions.formatCurrency(v) },
    { key: "status",         label: "Status",      render: (v)    => statusBadge(v) },
    { key: "id", label: "Actions", render: (id, row) => {
      const fy = fyList.find((y) => y.id === row.financial_year_id);
      const canEdit = row.status === "draft" && fy?.status === "open";
      return (
      <div className="d-flex gap-1">
        <button className="btn btn-sm btn-light" onClick={() => navigate(`/accounting/vouchers/${id}`)} title="View"><i className="fa-solid fa-eye"></i></button>
        {row.status === "draft" && (
          <RenderSecure code="ACC-VOUCHER-CREATE">
            <button className="btn btn-sm btn-success" onClick={() => handleApprove(id)} title="Approve"><i className="fa-solid fa-check"></i></button>
          </RenderSecure>
        )}
        {canEdit && (
          <>
            <button className="btn btn-sm btn-light tx-primary" onClick={() => navigate(`/accounting/vouchers/new?type=${row.type}&edit=${id}`)} title="Edit"><i className="fa-solid fa-pen"></i></button>
            <button className="btn btn-sm btn-light tx-danger" onClick={() => handleDelete(id)} title="Delete"><i className="fa-solid fa-trash"></i></button>
          </>
        )}
      </div>
      );
    }},
  ];

  const setLine = (i, field, val) => {
    const updated = [...lines];
    updated[i] = { ...updated[i], [field]: val };
    setLines(updated);
  };

  const addDebitLine  = () => setLines([...lines, { account_id: null, debit: "", credit: "", description: "", type: "debit" }]);
  const addCreditLine = () => setLines([...lines, { account_id: null, debit: "", credit: "", description: "", type: "credit" }]);

  return (
    <AppContainer title="Vouchers" previousPageTitle="Accounting"
      description="Payment (PV), Receipt (RV), and Journal (JV) vouchers. Each voucher auto-generates a journal entry when approved."
      actions={[
        { label: "New Voucher", icon: "fa-solid fa-plus", className: "btn btn-primary", onClick: () => setShowCreate(true) },
      ]}>
      <Toaster />

      <div className="card custom-card p-3 mb-3">
        <div className="row g-2">
          <div className="col-lg-3">
            <label className="form-label tx-12 fw-semibold">Financial Year</label>
            <Select options={fyOptions} value={selectedFY} onChange={setSelectedFY} placeholder="Select year" isSearchable />
          </div>
          <div className="col-lg-3">
            <label className="form-label tx-12 fw-semibold">Voucher Type</label>
            <Select options={[{ value: null, label: "All Types" }, ...VOUCHER_TYPES]} value={filterType} onChange={setFilterType} placeholder="All Types" isClearable />
          </div>
          <div className="col-lg-3">
            <label className="form-label tx-12 fw-semibold">Status</label>
            <Select options={[{ value: null, label: "All Status" }, ...statusOptions]} value={filterStatus} onChange={setFilterStatus} placeholder="All Status" isClearable />
          </div>
        </div>
      </div>

      <div className="card custom-card p-3">
        <ExportBar data={vouchers} columns={[
          { key: "voucher_number", label: "Voucher #" }, { key: "type", label: "Type" },
          { key: "voucher_date", label: "Date" }, { key: "payee_name", label: "Payee" },
          { key: "description", label: "Description" }, { key: "total_amount", label: "Amount" }, { key: "status", label: "Status" },
        ]} filename="vouchers" pdfTitle="Vouchers Report" printCode="ACC-VOUCHER" exportCode="ACC-VOUCHER" />
        <DataTable columns={columns} data={vouchers} loading={loading} pageSize={15} emptyMessage="No vouchers found." />
      </div>

      {/* CREATE VOUCHER MODAL */}
      {showCreate && (
        <AppModal
          title="Create Voucher"
          size="xl"
          onClose={() => { setShowCreate(false); resetForm(); }}
          onSubmit={handleCreate}
          footer={
            <>
              <button type="button" className="btn btn-light" onClick={() => { setShowCreate(false); resetForm(); }}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? "Creating..." : "Create Voucher"}</button>
            </>
          }
        >
          <div className="row g-3 mb-3">
            <div className="col-lg-3">
              <label className="form-label tx-12 fw-semibold">Voucher Type <span className="tx-danger">*</span></label>
              <Select options={VOUCHER_TYPES} value={vType} onChange={setVType} placeholder="Select type" />
            </div>
            <div className="col-lg-3">
              <label className="form-label tx-12 fw-semibold">Date</label>
              <input type="date" className="form-control" value={form.voucher_date} onChange={(e) => setForm({ ...form, voucher_date: e.target.value })} />
            </div>
            <div className="col-lg-3">
              <label className="form-label tx-12 fw-semibold">Reference</label>
              <input className="form-control" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="Cheque / Ref #" />
            </div>
            <div className="col-lg-3">
              <label className="form-label tx-12 fw-semibold">Payee Name</label>
              <input className="form-control" value={form.payee_name} onChange={(e) => setForm({ ...form, payee_name: e.target.value })} placeholder="e.g. Kampala Hardware" />
            </div>
            <div className="col-12">
              <label className="form-label tx-12 fw-semibold">Description <span className="tx-danger">*</span></label>
              <input className="form-control" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required />
            </div>
          </div>

          {/* Lines */}
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="fw-semibold mb-0">Account Lines</h6>
            <div className="d-flex gap-2">
              <button type="button" className="btn btn-sm btn-outline-success" onClick={addDebitLine}
                disabled={!canAddRow(withAmount(lines), JE_LINE_REQUIRED)}
                title={addRowBlockedReason(withAmount(lines), JE_LINE_REQUIRED) || "Add a debit line"}>
                <i className="fa-solid fa-plus me-1"></i>Add Dr Line
              </button>
              <button type="button" className="btn btn-sm btn-outline-danger" onClick={addCreditLine}
                disabled={!canAddRow(withAmount(lines), JE_LINE_REQUIRED)}
                title={addRowBlockedReason(withAmount(lines), JE_LINE_REQUIRED) || "Add a credit line"}>
                <i className="fa-solid fa-plus me-1"></i>Add Cr Line
              </button>
            </div>
          </div>
          <div className="table-responsive">
            <table className="table table-bordered" style={{ fontSize: "0.84rem" }}>
              <thead className="bg-light">
                <tr><th style={{ width: "50px" }}>Type</th><th style={{ minWidth: "220px" }}>Account</th><th>Description</th><th style={{ width: "130px" }}>Debit</th><th style={{ width: "130px" }}>Credit</th><th style={{ width: "38px" }}></th></tr>
              </thead>
              <tbody>
                {lines.map((line, i) => {
                  const isDebit = line.type !== "credit";
                  return (
                  <tr key={i}>
                    <td className="text-center align-middle">
                      <span className={`badge ${isDebit ? "bg-success" : "bg-danger"}`}>{isDebit ? "DR" : "CR"}</span>
                    </td>
                    <td>
                      <Select options={accountOptions} value={line.account_id} onChange={(v) => setLine(i, "account_id", v)} placeholder="Select account" />
                    </td>
                    <td><input className="form-control form-control-sm" value={line.description} onChange={(e) => setLine(i, "description", e.target.value)} /></td>
                    <td><input type="number" className="form-control form-control-sm" min="0" step="0.01" value={line.debit} disabled={!isDebit} onChange={(e) => setLine(i, "debit", e.target.value)} /></td>
                    <td><input type="number" className="form-control form-control-sm" min="0" step="0.01" value={line.credit} disabled={isDebit} onChange={(e) => setLine(i, "credit", e.target.value)} /></td>
                    <td>{lines.length > 2 && <button type="button" className="btn btn-sm btn-light tx-danger" onClick={() => setLines(lines.filter((_, j) => j !== i))}><i className="fa-solid fa-trash"></i></button>}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </AppModal>
      )}
    </AppContainer>
  );
};

export default Vouchers;
